import { useCallback, useEffect, useRef, useState } from 'react';

import { InstanceResponseModel } from '../../../../../../../apis/v1/instances/model';
import { INSTANCE_NAME_REGEX } from '../InstanceListTable.utils';

export type InstanceNameErrorKey = '' | 'required' | 'length' | 'invalid';

interface UseEditProfileFormProps {
    instances: InstanceResponseModel[];
}

interface UseEditProfileFormReturn {
    instanceNameValue: string;
    instanceNameError: InstanceNameErrorKey;
    instanceTagsValue: string[];
    isSaving: boolean;
    handleInstanceNameChange: (value: string) => void;
    setInstanceTagsValue: (tags: string[]) => void;
    resetForm: (instanceId: number | null) => void;
    handleSaveChanges: (
        instanceId: number | null,
        updateName: (id: number, hostname: string) => void,
        createTag: (id: number, tag: string) => void,
        deleteTag: (id: number, tagId: string) => void,
        onClose: () => void
    ) => void;
}

export function useEditProfileForm({
    instances,
}: UseEditProfileFormProps): UseEditProfileFormReturn {
    const [instanceNameValue, setInstanceNameValue] = useState('');
    const [instanceNameError, setInstanceNameError] = useState<InstanceNameErrorKey>('');
    const [instanceTagsValue, setInstanceTagsValue] = useState<string[]>([]);
    const originalInstanceTagsRef = useRef<string[]>([]);

    const handleInstanceNameChange = useCallback((value: string) => {
        setInstanceNameValue(value);

        if (!value) {
            setInstanceNameError('required');
        } else if (value.length < 4 || value.length > 20) {
            setInstanceNameError('length');
        } else if (!INSTANCE_NAME_REGEX.test(value)) {
            setInstanceNameError('invalid');
        } else {
            setInstanceNameError('');
        }
    }, []);

    const resetForm = useCallback(
        (instanceId: number | null) => {
            if (instanceId === null) return;
            const instance = instances.find((i) => i.id === instanceId);
            const hostname = instance?.hostname || '';
            const tagNames = (instance?.tags || []).map((tag) => tag.name);
            setInstanceNameValue(hostname);
            setInstanceTagsValue(tagNames);
            originalInstanceTagsRef.current = tagNames;
            setInstanceNameError('');
        },
        [instances]
    );

    const handleSaveChanges = useCallback(
        (
            instanceId: number | null,
            updateName: (id: number, hostname: string) => void,
            createTag: (id: number, tag: string) => void,
            deleteTag: (id: number, tagId: string) => void,
            onClose: () => void
        ) => {
            if (instanceId === null || instanceNameError) return;

            const instance = instances.find((i) => i.id === instanceId);
            if (!instance) return;

            if (instanceNameValue !== instance.hostname) {
                updateName(instanceId, instanceNameValue);
            }

            const originalTags = originalInstanceTagsRef.current;
            const tagsToAdd = instanceTagsValue.filter((tag) => !originalTags.includes(tag));
            const tagsToDelete = originalTags.filter((tag) => !instanceTagsValue.includes(tag));

            tagsToAdd.forEach((tag) => {
                createTag(instanceId, tag);
            });

            tagsToDelete.forEach((tagName) => {
                const tagToDelete = instance.tags.find((t) => t.name === tagName);
                if (tagToDelete) {
                    deleteTag(instanceId, tagToDelete.name as any);
                }
            });

            onClose();
        },
        [instanceNameValue, instanceNameError, instanceTagsValue, instances]
    );

    return {
        instanceNameValue,
        instanceNameError,
        instanceTagsValue,
        isSaving: false,
        handleInstanceNameChange,
        setInstanceTagsValue,
        resetForm,
        handleSaveChanges,
    };
}
